import React from 'react';
import {Button, Card, Divider, makeStyles, Spinner, Text, Title3, tokens,} from '@fluentui/react-components';
import {useApiMutation} from "../../services/useApiQuery.ts";
import {useTranslation} from "react-i18next";

const useStyles = makeStyles({
    container: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        padding: '16px',
        maxWidth: '400px',
        margin: '0 auto',
    },
    code: {
        fontFamily: tokens.fontFamilyMonospace,
        fontSize: tokens.fontSizeBase500,
        letterSpacing: '0.08em',
    },
    errorMessage: {
        color: tokens.colorPaletteRedForeground1,
    },
});

type TenantLinkRequestResponse = {
    code?: string;
    expiresAt?: number;
    errorCode?: string;
    retryAfterMs?: number;
};

export interface AccessKeyPromptProps {
    recheckAccess: () => void;
    selfServiceSignupUrl: string;
}

export const AccessKeyPrompt: React.FC<AccessKeyPromptProps> = ({recheckAccess, selfServiceSignupUrl}) => {
    const styles = useStyles();
    const {t} = useTranslation();

    const tenantLinkRequestMutation = useApiMutation<TenantLinkRequestResponse>('POST', {
        skipErrorNotification: true,
    });

    const handleConnectOrganization = () => {
        tenantLinkRequestMutation.mutate({
            path: '/api/access/tenant-link-request',
            body: {},
        });
    };

    const response = tenantLinkRequestMutation.data;
    const linkError = tenantLinkRequestMutation.error as (Error & { errorCode?: string; retryAfterMs?: number }) | null;
    const errorCode = response?.errorCode ?? linkError?.errorCode;
    const retryAfterMs = response?.retryAfterMs ?? linkError?.retryAfterMs;
    const cooldownMinutes = retryAfterMs != null
        ? Math.max(1, Math.ceil(retryAfterMs / 60_000))
        : undefined;

    React.useEffect(() => {
        if (errorCode === "tenant.already_mapped") {
            recheckAccess();
        }
    }, [errorCode, recheckAccess]);

    React.useEffect(() => {
        window.addEventListener("focus", recheckAccess);
        return () => window.removeEventListener("focus", recheckAccess);
    }, [recheckAccess]);

    React.useEffect(() => {
        if (!response?.code) {
            return;
        }
        const intervalId = window.setInterval(() => recheckAccess(), 15_000);
        return () => window.clearInterval(intervalId);
    }, [response?.code, recheckAccess]);

    return (
        <Card>
            <div className={styles.container}>
                <Title3>{t("text.start-evaluation-desc")}</Title3>
                <Button
                    appearance="primary"
                    as="a"
                    href={selfServiceSignupUrl}
                    target="_blank"
                    rel="noreferrer"
                >
                    {t("button.set-up-arvame")}
                </Button>
                <Divider />
                <Title3>{t("text.connect-organization-title")}</Title3>
                <Text>{t("text.connect-organization-desc")}</Text>
                {response?.code && response.expiresAt ? (
                    <>
                        <Text className={styles.code}>{response.code}</Text>
                        <Text>{t("text.connect-organization-expires", {
                            expires: new Date(response.expiresAt).toUTCString(),
                        })}</Text>
                        <Text>{t("text.connect-organization-pass-to-contact")}</Text>
                        <Button
                            appearance="secondary"
                            disabled={tenantLinkRequestMutation.isPending}
                            onClick={handleConnectOrganization}
                        >
                            {tenantLinkRequestMutation.isPending
                                ? <Spinner size="tiny"/>
                                : t("button.connect-organization")}
                        </Button>
                    </>
                ) : errorCode === "tenant_link.rotation_cooldown" && cooldownMinutes != null ? (
                    <Text>{t("text.connect-organization-cooldown", {minutes: cooldownMinutes})}</Text>
                ) : (
                    <Button
                        appearance="secondary"
                        disabled={tenantLinkRequestMutation.isPending}
                        onClick={handleConnectOrganization}
                    >
                        {tenantLinkRequestMutation.isPending
                            ? <Spinner size="tiny"/>
                            : t("button.connect-organization")}
                    </Button>
                )}
                {tenantLinkRequestMutation.isError && errorCode !== "tenant_link.rotation_cooldown" && errorCode !== "tenant.already_mapped" && (
                    <Text className={styles.errorMessage}>
                        {t("error.connect-organization-failed")}
                    </Text>
                )}
                {errorCode === "tenant.already_mapped" && (
                    <Text className={styles.errorMessage}>
                        {t("error.connect-organization-already-mapped")}
                    </Text>
                )}
            </div>
        </Card>
    );
};

export default AccessKeyPrompt;
